import { useState } from 'react';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { X, CheckCircle, AlertTriangle } from 'lucide-react';
import { calcRoute, bestStop } from '@/lib/pcmiler';

// Prefers the full pickup/dropoff street address over the coarse
// origin/destination "City, ST" pair -- see bestStop's own comment. Two
// stops in the same city but different addresses (a local/drayage move)
// otherwise collapse to a 0-mile route and get wrongly flagged as failed.
async function runTrimbleStateReport(origin, destination, pickupAddress, dropoffAddress) {
  if (!origin || !destination) return [];
  const result = await calcRoute(
    [bestStop(pickupAddress, origin), bestStop(dropoffAddress, destination)],
    { includeStates: true }
  );
  if (result.error) {
    console.error('Trimble fetch failed:', result.error);
    return [];
  }
  return result.stateMileage || [];
}

function getQuarterFromDate(dateStr) {
  const d = new Date(dateStr);
  const q = Math.ceil((d.getMonth() + 1) / 3);
  return { quarter: `Q${q}-${d.getFullYear()}`, year: d.getFullYear(), d };
}

// mode: 'all' = backfill unprocessed delivered loads
//       'retry' = retry Manual logs with empty state_mileage
export default function IFTABackfillModal({ mode = 'all', onClose, onComplete }) {
  const { user } = useAuth();
  const [status, setStatus] = useState('idle'); // idle | running | done | error
  const [message, setMessage] = useState('');
  const [progress, setProgress] = useState(0); // 0-100
  const [summary, setSummary] = useState('');

  const run = async () => {
    setStatus('running');
    setProgress(0);

    if (mode === 'all') {
      // Fetch all delivered loads for this driver. 'Delivered' is a
      // transient status -- a dispatcher normally moves it on to
      // 'Invoiced' (and possibly 'Claim') shortly after, per
      // EditLoadModal's status options. Matching only 'Delivered' meant
      // any load already invoiced by the time this ran was invisible
      // here -- combined with the delete-empty-logs step below, that
      // silently destroyed an invoiced load's mileage log with no way
      // to recreate it.
      setMessage('Fetching your delivered loads…');
      const allLoads = await client.entities.Load.list('-updated_date', 200);
      const DELIVERED_OR_LATER = new Set(['Delivered', 'Invoiced', 'Claim']);
      const loads = allLoads.filter(
        l =>
          l.assigned_driver_email?.toLowerCase() === user.email.toLowerCase() &&
          DELIVERED_OR_LATER.has(l.status)
      );

      // Fetch existing IFTA logs to skip already-processed
      setMessage('Checking existing mileage logs…');
      const existing = await client.entities.IFTAMileageLog.filter(
        { driver_email: user.email },
        '-created_date',
        200
      );
      const processedIds = new Set(existing.map(r => r.load_id));

      // Delete empty logs so they get recreated with real data
      const emptyLogs = existing.filter(
        log => !log.state_mileage || log.state_mileage === '[]'
      );
      for (const log of emptyLogs) {
        await client.entities.IFTAMileageLog.delete(log.id);
        processedIds.delete(log.load_id);
      }
      setMessage(`Cleaned up ${emptyLogs.length} empty logs. Starting fresh import…`);

      const toProcess = loads.filter(l => !processedIds.has(l.id));

      if (toProcess.length === 0) {
        setSummary('All delivered loads are already imported.');
        setStatus('done');
        return;
      }

      const quartersSet = new Set();
      for (let i = 0; i < toProcess.length; i++) {
        const load = toProcess[i];
        setMessage(`Processing load ${i + 1} of ${toProcess.length} — Load #${load.load_number || load.id}`);
        setProgress(Math.round(((i) / toProcess.length) * 100));

        const tripDateRaw = load.pickup_date || load.updated_date || load.created_date;
        const tripDate = tripDateRaw ? tripDateRaw.slice(0, 10) : new Date().toISOString().slice(0, 10);
        const { quarter, year } = getQuarterFromDate(tripDate);
        quartersSet.add(quarter);

        let stateMileage = [];
        try {
          stateMileage = await runTrimbleStateReport(load.origin, load.destination, load.pickup_address, load.dropoff_address);
        } catch (err) {
          console.error('Trimble call failed for load:', load?.load_number, err?.message || err);
        }

        const totalMiles = stateMileage.reduce((s, x) => s + x.miles, 0);
        await client.entities.IFTAMileageLog.create({
          load_id: load.id,
          load_number: load.load_number || '',
          driver_email: user.email,
          origin: load.origin || '',
          destination: load.destination || '',
          trip_date: tripDate,
          quarter,
          year,
          state_mileage: JSON.stringify(stateMileage),
          total_miles: totalMiles,
          source: stateMileage.length > 0 ? 'PCMiler-Backfill' : 'Manual',
        });

        await new Promise(resolve => setTimeout(resolve, 300));
      }

      setProgress(100);
      setSummary(`Done! Imported ${toProcess.length} load${toProcess.length !== 1 ? 's' : ''} across ${quartersSet.size} quarter${quartersSet.size !== 1 ? 's' : ''}.`);
      setStatus('done');

    } else if (mode === 'retry') {
      setMessage('Finding logs that need retry…');
      const [failedLogs, allLoads] = await Promise.all([
        client.entities.IFTAMileageLog.filter({ driver_email: user.email }, '-created_date', 200),
        client.entities.Load.list('-created_date', 200),
      ]);

      const toRetry = failedLogs.filter(log => !log.state_mileage || log.state_mileage === '[]');

      if (toRetry.length === 0) {
        setSummary('No failed logs found — all records have state data.');
        setStatus('done');
        return;
      }

      const loadMap = {};
      allLoads.forEach(l => { loadMap[l.id] = l; });

      const isValid = (str) => str && str.length >= 3 && /[a-zA-Z]/.test(str) && str.trim() !== '';

      let fixedCount = 0;
      let skippedCount = 0;

      for (let i = 0; i < toRetry.length; i++) {
        const log = toRetry[i];
        setMessage(`Retrying ${i + 1} of ${toRetry.length} — Load #${log.load_number || log.load_id}`);
        setProgress(Math.round((i / toRetry.length) * 100));

        const load = loadMap[log.load_id];

        console.log('Retrying load:', {
          log_id: log.id,
          load_id: log.load_id,
          load_found: !!load,
          origin: load?.origin,
          destination: load?.destination,
          isValidOrigin: isValid(load?.origin || ''),
          isValidDest: isValid(load?.destination || ''),
        });

        if (!load) {
          skippedCount++;
          continue;
        }

        const origin = load.origin || '';
        const destination = load.destination || '';

        if (!isValid(origin) || !isValid(destination)) {
          await client.entities.IFTAMileageLog.update(log.id, {
            source: 'Invalid-Data',
            origin,
            destination,
          });
          skippedCount++;
          continue;
        }

        try {
          const stateMileage = await runTrimbleStateReport(origin, destination, load.pickup_address, load.dropoff_address);
          const totalMiles = stateMileage.reduce((sum, s) => sum + s.miles, 0);

          await client.entities.IFTAMileageLog.update(log.id, {
            origin,
            destination,
            state_mileage: JSON.stringify(stateMileage),
            total_miles: totalMiles,
            source: stateMileage.length > 0 ? 'PCMiler-Backfill' : 'Manual',
          });
          fixedCount++;
        } catch (err) {
          console.error('Retry failed for load:', log?.load_number, err?.message || err);
          skippedCount++;
        }

        await new Promise(r => setTimeout(r, 300));
      }

      setProgress(100);
      setSummary(
        `Done!\n✅ Fixed: ${fixedCount}\n⚠️ Invalid locations (test data): ${skippedCount}\n📋 Total processed: ${toRetry.length}`
      );
      setStatus('done');
    }
  };

  const handleDone = () => {
    onComplete();
    onClose();
  };

  const title = mode === 'retry' ? 'Retry Failed Mileage Logs' : 'Importing Historical Mileage';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={status !== 'running' ? onClose : undefined} />
      <div className="relative bg-card border border-border rounded-2xl shadow-xl w-full max-w-md z-10 p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="font-space font-semibold text-foreground">{title}</h2>
          {status !== 'running' && (
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {status === 'idle' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {mode === 'all'
                ? 'This will scan all your delivered loads and create IFTA mileage logs using PC*Miler. Already-imported loads will be skipped.'
                : 'This will retry PC*Miler lookups for any mileage logs that were saved without state-level data.'}
            </p>
            <p className="text-xs text-amber-400/80 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
              This may take a few minutes depending on how many loads need processing.
            </p>
            <div className="flex gap-3 pt-1">
              <button
                onClick={run}
                className="flex-1 py-2.5 rounded-lg bg-amber-500 text-black text-sm font-bold hover:bg-amber-400 transition-colors"
              >
                Start Import
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2.5 rounded-lg border border-border text-muted-foreground text-sm hover:text-foreground hover:border-border transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {status === 'running' && (
          <div className="space-y-4">
            <p className="text-sm text-foreground leading-relaxed min-h-[2.5rem]">{message}</p>
            <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full bg-amber-500 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground text-center">{progress}% complete — please don't close this window</p>
          </div>
        )}

        {status === 'done' && (
          <div className="space-y-4 text-center">
            <CheckCircle className="w-10 h-10 text-green-400 mx-auto" />
            <p className="text-sm text-foreground font-medium whitespace-pre-line">{summary}</p>
            <button
              onClick={handleDone}
              className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
            >
              Close & Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  );
}